import React, { useContext, useEffect } from 'react';
import { ForecastScreen } from './forecast-screen/ForecastScreen';
import { GoalIndicationScreen } from './goal-indication-screen/GoalIndicationScreen';
import { MemoryRouter, Redirect, Route } from 'react-router-dom';
import { GoalPage, GOALS } from '../../config/goals';
import { GoalQuestion } from './goal-indication-screen/goal-question/GoalQuestion';
import { WithForecastContext } from '../../contexts/investment-robot/ForecastContext';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
    GoalIndicationContext,
    WithGoalIndicationContext,
} from '../../contexts/investment-robot/GoalIndicationContext';
import { SwitchWithPageTracking } from '../../utils/hooks';
import { CLIENT_DETAILS_OPTIONS, ROBOT_ENVIRONMENT } from '../../config/constants';
import { RobotEnvironmentContext, WithRobotEnvironment } from '../../contexts/general/RobotEnvironmentContext';
import { getGeneralPages, getGoalSpecificPages } from '../../utils/functions';
import { WithContactDetails } from '../../contexts/general/ContactDetailsContext';
import { ForecastResultsCalculations } from './forecast-screen/ForecastResultsCalculations';
import { prepareHostSite } from '../../utils/prepareBNDwebsite';

const queryClient = new QueryClient();

export const InvestmentRobot: React.FC<{
    environment: ROBOT_ENVIRONMENT;
    googleAnalyticsCode?: string;
    contactDetailsOption?: CLIENT_DETAILS_OPTIONS;
}> = ({ environment, googleAnalyticsCode, contactDetailsOption }) => {
    // Prepare the marketing site for the display of this tool
    useEffect(() => {
        prepareHostSite();
    }, []);

    const skipGoalIndication =
        new URLSearchParams(window.location.search).get('skipGoalIndication') === 'true' ? true : false;

    return (
        <div className="relative text-bnd-black investment-robot text-base font-light leading-normal text-left mx-force-edge xl:p-8">
            <div className="max-w-screen-3xl mx-auto">
                <QueryClientProvider client={queryClient}>
                    <WithContactDetails>
                        <WithRobotEnvironment
                            environment={environment}
                            googleAnalyticsCode={googleAnalyticsCode}
                            contactDetailsOption={contactDetailsOption}
                        >
                            <WithForecastContext>
                                <WithGoalIndicationContext>
                                    <MemoryRouter initialEntries={skipGoalIndication ? ['/forecast'] : ['/']}>
                                        <SwitchWithPageTracking prefix="/investment-robot">
                                            <Route exact path="/forecast">
                                                <ForecastScreen />
                                            </Route>
                                            <Route exact path="/summary">
                                                <ForecastResultsCalculations className={''} />
                                            </Route>
                                            <Route exact path="/goal-indication">
                                                <GoalIndicationScreen />
                                            </Route>
                                            {GOALS.map((goal) => {
                                                return getGoalSpecificPages(goal.pages, contactDetailsOption).map(
                                                    (page, index) => {
                                                        return (
                                                            <Route
                                                                exact
                                                                path={page.routes.current}
                                                                key={page.routes.current}
                                                            >
                                                                <GoalQuestion
                                                                    goal={goal}
                                                                    goalPage={page}
                                                                    goalPageIndex={index}
                                                                />
                                                            </Route>
                                                        );
                                                    },
                                                );
                                            })}
                                            {getGeneralPages(contactDetailsOption).map((page, index) => {
                                                return (
                                                    <Route exact path={page.routes.current} key={page.routes.current}>
                                                        <GeneralGoalQuestion page={page} generalPageIndex={index} />
                                                    </Route>
                                                );
                                            })}
                                            <Route path="/">
                                                <Redirect to="/goal-indication" />
                                            </Route>
                                        </SwitchWithPageTracking>
                                    </MemoryRouter>
                                </WithGoalIndicationContext>
                            </WithForecastContext>
                        </WithRobotEnvironment>
                    </WithContactDetails>
                </QueryClientProvider>
            </div>
        </div>
    );
};

const GeneralGoalQuestion: React.FC<{ page: GoalPage; generalPageIndex: number }> = ({ page, generalPageIndex }) => {
    const goalIndicationContext = useContext(GoalIndicationContext);
    const robotEnvironmentContext = useContext(RobotEnvironmentContext);
    const selectedGoal = GOALS.find((g) => g.id === goalIndicationContext.parameters.selectedGoal) || GOALS[0];

    const maybeOneExtraForAddedClientDetailPage = () => {
        if (
            robotEnvironmentContext.contactDetailsOption === 'last' ||
            robotEnvironmentContext.contactDetailsOption === 'first'
        )
            return 1;
        return 0;
    };

    return (
        <GoalQuestion
            goal={selectedGoal}
            goalPage={page}
            goalPageIndex={selectedGoal.pages.length + generalPageIndex + maybeOneExtraForAddedClientDetailPage()}
        />
    );
};
